import Image from 'next/image';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Card,
  CardBody,
  Flex,
  Heading,
  LinkOverlay,
  List,
  ListItem,
  Text,
  VisuallyHidden,
} from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';
import { CardCarousel } from '~/features/promotions/components/card-carousel';
import { SitewideSalePromoCard } from '~/features/promotions/components/sitewide-sale-promo-card';
import type { Summer2026SaleVariant } from '~/features/promotions/constants/summer-2026-sale';
import { useSummer2026SaleVariant } from '~/features/promotions/hooks/use-summer-2026-sale-variant';
import { getSummer2026SaleSlideKey } from '~/features/promotions/utils/get-summer-2026-sale-variant';

import { CircleArrowRightIcon, RocketIcon } from '../icons';

import { AgenticAiPromoCard } from './agentic-ai-promo-card';
import { MastersAIPromoCard, MbaAiPromoCard, PromoCard } from './promo-card';
import { SectionHeader } from './section-header';

type UdacityImpactPromoKey =
  | 'agentic-ai'
  | 'generative-ai'
  | 'mba-ai-product-management'
  | 'masters-artificial-intelligence';

const DEFAULT_IMPACT_PROMO_KEYS: UdacityImpactPromoKey[] = [
  'agentic-ai',
  'generative-ai',
  'mba-ai-product-management',
  'masters-artificial-intelligence',
];

export function UdacityImpactSection({
  promoItemKeysOverride,
}: {
  promoItemKeysOverride?: UdacityImpactPromoKey[];
} = {}) {
  const { t } = useTranslation();
  const { isSummer2026GateReady, promoCode, summer2026Variant } = useSummer2026SaleVariant();
  const FEATURE_CARD_BACKGROUND_SHAPE = [
    '/images/experiments/ab_plp_redesign_2025/vertical-lines.svg',
    '/images/experiments/ab_plp_redesign_2025/ripple.svg',
    '/images/experiments/ab_plp_redesign_2025/diagonal-lines.svg',
  ] as const;

  const promoItems: Array<{ key: string; summer2026Variant?: Summer2026SaleVariant }> = [
    ...(summer2026Variant ? [{ key: getSummer2026SaleSlideKey(summer2026Variant), summer2026Variant }] : []),
    ...(promoItemKeysOverride ?? DEFAULT_IMPACT_PROMO_KEYS).map((key) => ({ key })),
  ];

  const features = [
    {
      titleKey: 'home.impact.features.handsOnLearning.title',
      subtitleKey: 'home.impact.features.handsOnLearning.subtitle',
      descriptionKey: 'home.impact.features.handsOnLearning.description',
    },
    {
      titleKey: 'home.impact.features.personalizedSupport.title',
      subtitleKey: 'home.impact.features.personalizedSupport.subtitle',
      descriptionKey: 'home.impact.features.personalizedSupport.description',
    },
    {
      titleKey: 'home.impact.features.measurableOutcomes.title',
      subtitleKey: 'home.impact.features.measurableOutcomes.subtitle',
      descriptionKey: 'home.impact.features.measurableOutcomes.description',
    },
  ] as const;

  return (
    <Box
      bgColor='grey.50'
      padding={{
        base: '56px 0px',
        md: '120px 0px',
      }}
    >
      <ContentContainer
        contentMaxWidth='content-max-width-md'
        display='flex'
        flexDirection='column'
        gap={{
          base: '56px',
          md: '104px',
        }}
      >
        <CardCarousel
          autoplayStartDelayMs={5000}
          isReady={isSummer2026GateReady}
          items={promoItems}
          renderCard={({ item, index }) => {
            if (item.summer2026Variant) {
              return (
                <SitewideSalePromoCard
                  key={item.key}
                  imagePriority={index === 0}
                  promoCode={promoCode}
                  variant={item.summer2026Variant}
                  onClick={() =>
                    trackEvent({
                      type: 'click',
                      name: 'Homepage CTA Clicked',
                      label: 'summer_2026_sale_clicked',
                    })
                  }
                />
              );
            }

            switch (item.key) {
              case 'mba-ai-product-management':
                return (
                  <MbaAiPromoCard
                    key={index}
                    onClick={() => {
                      trackEvent({
                        type: 'click',
                        name: 'Homepage CTA Clicked',
                        label: 'mba_entry_clicked',
                        additionalAttributes: {
                          formal_degree_key: 'mba_ai',
                        },
                      });
                    }}
                  />
                );
              case 'generative-ai': {
                const handleGenAiClick = () =>
                  trackEvent({
                    type: 'click',
                    name: 'Homepage CTA Clicked',
                    label: 'enroll_now_clicked',
                    value: 'New at Udacity',
                    additionalAttributes: {
                      degree_key: 'nd608',
                    },
                  });
                return (
                  <PromoCard
                    key={item.key}
                    backgroundColor='grey.900'
                    id='generative-ai-title'
                    backgroundImageSlot={
                      <Box
                        bottom={0}
                        left={0}
                        position='absolute'
                        right={0}
                        top={0}
                        opacity={{
                          base: 0.5,
                          md: 1,
                        }}
                      >
                        <Image
                          alt=''
                          src='/images/experiments/ab_plp_redesign_2025/generative-ai-bg.png'
                          style={{
                            objectFit: 'cover',
                            objectPosition: 'bottom',
                          }}
                          fill
                        />
                      </Box>
                    }
                    buttonSlot={
                      <ButtonLink
                        linkProps={{ href: '/course/generative-ai--nd608' }}
                        buttonProps={{
                          size: 'icon-left',
                          leftIcon: <CircleArrowRightIcon height='18px' width='18px' />,
                          variant: 'solid',
                          colorScheme: 'blue',
                          position: 'relative',
                          zIndex: 1,
                          width: {
                            base: '100%',
                            md: '152px',
                          },
                          onClick: handleGenAiClick,
                        }}
                      >
                        {t('common.learnMore')}
                        <VisuallyHidden>{t('home.impact.generativeAi.learnMoreAria')}</VisuallyHidden>
                      </ButtonLink>
                    }
                    contentSlot={
                      <Box>
                        <Heading
                          as='h2'
                          color='white'
                          fontWeight='600'
                          id='generative-ai-title'
                          mb={3}
                          fontSize={{
                            base: '1.75rem',
                            md: '2.25rem',
                          }}
                          lineHeight={{
                            base: '1.14',
                            md: '1.1',
                          }}
                        >
                          <LinkOverlay href='/course/generative-ai--nd608' onClick={handleGenAiClick}>
                            {t('home.impact.generativeAi.title')}
                          </LinkOverlay>
                        </Heading>
                        <Text color='grey.300'>{t('home.impact.generativeAi.description')}</Text>
                      </Box>
                    }
                    header={{
                      color: 'cyan.300',
                      lineColor: 'cyan.300',
                    }}
                    padding={{
                      base: '32px',
                      md: '48px 48px 56px',
                    }}
                  />
                );
              }
              case 'masters-artificial-intelligence':
                return (
                  <MastersAIPromoCard
                    key={index}
                    onClick={() => {
                      trackEvent({
                        type: 'click',
                        name: 'Homepage CTA Clicked',
                        label: 'masters_entry_clicked',
                        additionalAttributes: {
                          formal_degree_key: 'ms_ai',
                        },
                      });
                    }}
                  />
                );
              case 'agentic-ai':
                return <AgenticAiPromoCard key={index} />;
              default:
                return null;
            }
          }}
          autoplayStartOnViewport
        />

        <Flex
          flexDirection='column'
          gap={{
            base: '32px',
            md: '72px',
          }}
        >
          <SectionHeader
            color='grey.500'
            descriptionSlot={<Text color='grey.500'>{t('home.impact.description')}</Text>}
            iconSlot={<RocketIcon color='grey.500' height='18px' width='18px' />}
            subtitle={t('home.impact.subtitle') ?? ''}
            containerProps={{
              maxWidth: '936px',
              width: '100%',
              margin: '0 auto',
            }}
            titleSlot={
              <Heading
                as='h2'
                fontWeight='600'
                lineHeight='1.1'
                fontSize={{
                  base: '2.25rem',
                  md: '3.375rem',
                }}
              >
                {t('home.impact.title')}
              </Heading>
            }
          />
          <List
            display='flex'
            flexWrap='wrap'
            justifyContent='center'
            flexDirection={{
              base: 'column',
              md: 'row',
            }}
            gap={{
              base: 4,
              md: 12,
            }}
          >
            {features.map((feature, idx) => (
              <Card
                key={idx}
                as={ListItem}
                bgColor='white'
                borderRadius='8px'
                boxShadow='none'
                display='flex'
                flexDirection='column'
                flex={{
                  md: '0 1 320px',
                }}
                padding={{
                  md: '8px 8px 40px 8px',
                }}
              >
                <Flex
                  alignItems='flex-start'
                  bgGradient='linear(90deg, #175CFF 0%, #00298C 100%)'
                  borderRadius='8px'
                  flexDirection='column'
                  justifyContent='flex-end'
                  overflow='hidden'
                  position='relative'
                  flex={{
                    md: '0 0 152px',
                  }}
                  padding={{
                    base: '16px',
                    md: '24px',
                  }}
                >
                  <Box
                    inset={0}
                    mixBlendMode='overlay'
                    position='absolute'
                    marginLeft={{
                      base: 'auto',
                      md: 'unset',
                    }}
                    maxWidth={{
                      base: '280px',
                      md: '100%',
                    }}
                  >
                    <Image
                      alt=''
                      src={FEATURE_CARD_BACKGROUND_SHAPE[idx % FEATURE_CARD_BACKGROUND_SHAPE.length]}
                      style={{
                        objectFit: 'cover',
                        display: 'block',
                        pointerEvents: 'none',
                      }}
                      fill
                    />
                  </Box>
                  <Heading
                    color='white'
                    fontWeight='600'
                    position='relative'
                    fontSize={{
                      base: '1.375rem',
                      md: '2rem',
                    }}
                    lineHeight={{
                      base: '1.2',
                      md: '1',
                    }}
                  >
                    {t(feature.titleKey)}
                  </Heading>
                </Flex>

                <CardBody
                  display='flex'
                  flexDirection='column'
                  gap={{
                    base: 4,
                    md: 6,
                  }}
                  padding={{
                    base: '16px',
                    md: '24px',
                  }}
                >
                  <Heading
                    as='p'
                    borderBottom='1px solid'
                    borderBottomColor='grey.100'
                    color='blue.500'
                    flexBasis='44px'
                    fontSize='1.125rem'
                    fontWeight='600'
                    lineHeight='1.1'
                  >
                    {t(feature.subtitleKey) ?? ''}
                  </Heading>
                  <Text color='grey.500'>{t(feature.descriptionKey)}</Text>
                </CardBody>
              </Card>
            ))}
          </List>
        </Flex>
      </ContentContainer>
    </Box>
  );
}
